// Browser interpretation of immutable source facts. No model-name inference.
import { clientSurfaceIdentity, clientSurfaceLabel } from "./clientSurfaceIdentity.js";
import { EVIDENCE_DIMENSIONS, SdkLanguage, resolveEvidenceDimension, summarizeEvidenceDimensions } from "./sourceEvidence.js";
import { HARNESS_CATALOG, harnessCandidates, harnessCategory } from "./harnessCatalog.js";
import { destinationService, reportedService } from "./inferenceServiceIdentity.js";
import { uuidToEndpointId } from "./inventoryRows.js";

const CONNECTOR_SERVICES = { openai_sync: "openai", anthropic_sync: "anthropic", copilot_sync: "microsoft", perplexity_sync: "perplexity", vertex_sync: "vertex-ai" };
const SERVICE_KEYS = ["dashboard.provider", "otlp.record.gen_ai.provider.name", "otlp.resource.gen_ai.provider.name", "connector.langsmith.ls_provider"];
const SYSTEM_KEYS = ["otlp.record.gen_ai.system", "otlp.resource.gen_ai.system"];
const ADDRESS_KEYS = ["otlp.record.server.address", "otlp.resource.server.address"];
const PROVIDER_KEYS = [...SERVICE_KEYS, ...SYSTEM_KEYS, ...ADDRESS_KEYS];
const lookup = (mapping, key) => Object.hasOwn(mapping, key) ? mapping[key] : undefined;

const READER = HARNESS_CATALOG.reader;
const EXCLUDED_SCOPES = new Set(READER.excludedScopes);
const BINDING_CONTEXT = new RegExp(READER.bindingContext);
const VALUE_TYPES = { stringValue: "string", booleanValue: "boolean" };
const bundleBytes = bundle => new TextEncoder().encode(JSON.stringify(bundle)).length;

// Mirrors the SQL reader in shared-analytics: the same filters, binding keys
// and bundle limits, so the dashboard never names a harness the API cannot.
export function readSourceFacts(entry) {
  const records = new Map(), bindings = new Map(), providerBindings = new Map(), sources = new Map();
  // ClickHouse groups identical redeliveries before counting, so we do too.
  const bundles = [...new Map((entry?.factBundles || []).map(bundle => [JSON.stringify(bundle), bundle])).values()];
  // An oversized bundle reaches SQL as unavailable; the size is measured on
  // the re-serialized JSON because the stored string is not sent here.
  const unavailable = bundles.length > READER.maxBundles
    || bundles.some(bundle => bundle?.unavailable === true || bundleBytes(bundle) > READER.maxBundleJsonBytes);
  const observationId = entry?.sourceEventId ? uuidToEndpointId(entry.sourceEventId) : null;
  for (const bundle of unavailable ? [] : bundles) {
    if (Number(bundle.schemaMajor) !== READER.schemaMajor || !bundle.observationId || bundle.observationId !== observationId) continue;
    for (const fact of bundle.facts || []) {
      const provenance = fact.provenance;
      if (provenance?.observationId !== bundle.observationId || !provenance.bindingId) continue;
      if (bundle.sourceKind && provenance.sourceKind && bundle.sourceKind !== provenance.sourceKind) continue;
      const scope = provenance.scope;
      if (EXCLUDED_SCOPES.has(scope)) continue;
      // Instrumentation scopes and resources use the same wire enum but bind different objects.
      const context = BINDING_CONTEXT.exec(fact.name)?.[1] || "";
      const bindingScope = `${scope}:${context}`;
      if (!bindings.has(bindingScope)) bindings.set(bindingScope, new Set());
      bindings.get(bindingScope).add(provenance.bindingId);
      if (PROVIDER_KEYS.includes(fact.name)) {
        if (!providerBindings.has(bindingScope)) providerBindings.set(bindingScope, new Set());
        providerBindings.get(bindingScope).add(provenance.bindingId);
      }
      const scalarKeys = READER.scalarKeys.filter(key => fact[key] != null);
      const expectedKey = lookup(READER.typedValueKeys, fact.name) || READER.defaultValueKey;
      const value = scalarKeys.length === 1 && scalarKeys[0] === expectedKey && typeof fact[expectedKey] === VALUE_TYPES[expectedKey] ? fact[expectedKey] : null;
      if (!records.has(fact.name)) records.set(fact.name, []);
      records.get(fact.name).push({ value, fact });
      const sourceKind = bundle.sourceKind || provenance.sourceKind;
      if (value !== null && sourceKind && provenance.sourceKind === sourceKind) {
        if (!sources.has(sourceKind)) sources.set(sourceKind, []);
        sources.get(sourceKind).push(fact);
      }
    }
  }
  return { records, bindings, providerBindings, sources, unavailable };
}

function interpretProvider(records, bindings, sources, sourceBindingConflict) {
  const evidence = PROVIDER_KEYS.flatMap(key => (records.get(key) || []).map(record => ({ value: record.value, facts: [record.fact] })));
  if ([...bindings.values()].some(ids => ids.size > 1)) return { conflict: true, evidence };
  for (const keys of [ADDRESS_KEYS, SERVICE_KEYS, SYSTEM_KEYS]) {
    const raw = keys.flatMap(key => (records.get(key) || []).map(record => ({ key, ...record })));
    if (!raw.length) continue;
    const labels = raw.map(({ key, value }) => ADDRESS_KEYS.includes(key) ? destinationService(value)
      : reportedService(value, key === "connector.langsmith.ls_provider" ? "langsmith" : ""));
    const known = new Set(labels.filter(Boolean));
    if (known.size > 1) return { conflict: true, evidence };
    return { provider: labels.every(Boolean) ? [...known][0] : null, evidence };
  }
  // These collectors read a service-specific API. A generic telemetry collector does not.
  const services = new Set([...sources.keys()].map(kind => lookup(CONNECTOR_SERVICES, kind)).filter(Boolean));
  const connectorEvidence = [...sources].filter(([kind]) => lookup(CONNECTOR_SERVICES, kind)).map(([kind, facts]) => ({ value: lookup(CONNECTOR_SERVICES, kind), facts: facts.slice(0, 1) }));
  return { provider: services.size === 1 && !sourceBindingConflict ? [...services][0] : undefined, conflict: services.size > 1 || Boolean(services.size && sourceBindingConflict), evidence: connectorEvidence };
}

export function interpretSourceObservation(entry) {
  const { records, bindings, providerBindings, sources } = readSourceFacts(entry);
  const all = key => records.get(key) || [];
  const get = key => {
    const values = new Set(all(key).map(record => record.value));
    return values.size === 1 ? [...values][0] : undefined;
  };
  const facts = (...keys) => keys.flatMap(key => all(key).map(record => record.fact));
  const claims = Object.fromEntries(Object.keys(EVIDENCE_DIMENSIONS).map(key => [key, []]));
  const claim = (key, value, evidence) => claims[key].push({ value, facts: evidence });
  const matched = harnessCandidates(records);
  let candidates = matched.candidates.map(candidate => ({ ...candidate, identity: clientSurfaceIdentity(candidate.surfaceId) }));
  for (const candidate of candidates) {
    for (const key of Object.keys(claims)) if (candidate.identity[key]) claim(key, candidate.identity[key], candidate.facts);
  }
  for (const record of all("request.anthropic.cc_entrypoint")) {
    if (!lookup(HARNESS_CATALOG.maps.ENTRYPOINTS, record.value)) claim("sdkLanguage", null, [record.fact]);
  }
  const recordType = get("connector.openai.record_type");
  if (["CONVERSATION_MESSAGE", "CODEX_LOG"].includes(recordType)) {
    const key = recordType === "CODEX_LOG" ? "connector.openai.client_id" : "connector.openai.client_type";
    const mapping = recordType === "CODEX_LOG" ? HARNESS_CATALOG.maps.OPENAI_CODEX_CLIENTS : HARNESS_CATALOG.maps.OPENAI_CLIENT_TYPES;
    for (const record of all(key)) if (!lookup(mapping, record.value)) claim("interface", null, [record.fact]);
  }
  if (get("connector.vertex.labels.bigquery_job_id_prefix")) claim("workload", "bigquery", facts("connector.vertex.labels.bigquery_job_id_prefix"));
  for (const [field, dimension] of [["sdk", "instrumentationSdk"], ["library", "framework"], ["runtime", "runtime"]]) {
    for (const record of all(`connector.langsmith.runtime.${field}`)) claim(dimension, record.value, [record.fact]);
  }
  for (const record of all("connector.langsmith.runtime.sdk")) {
    const language = lookup({ "langsmith-py": SdkLanguage.PYTHON, "langsmith-js": SdkLanguage.JAVASCRIPT }, record.value);
    if (language) claim("sdkLanguage", language, [record.fact]);
  }
  for (const [sourceKind, evidence] of sources) {
    if (Object.hasOwn(EVIDENCE_DIMENSIONS.captureSource.values, sourceKind)) claim("captureSource", sourceKind, evidence.slice(0, 1));
  }
  if (get("first_party.origin_dashboard") === true) claim("captureSource", "origin_dashboard", facts("first_party.origin_dashboard"));
  const bindingConflict = [...bindings.values()].some(ids => ids.size > 1);
  const dimensions = Object.fromEntries(Object.entries(claims).map(([key, values]) => [key, resolveEvidenceDimension(key, values, bindingConflict && values.length > 0)]));
  const refinedProducts = new Set(candidates.map(candidate => candidate.identity.parentProductId).filter(Boolean));
  candidates = candidates.filter(candidate => candidate.identity.host || !refinedProducts.has(candidate.identity.productId));
  const products = new Set(candidates.map(candidate => candidate.identity.productId));
  const conflict = bindingConflict || matched.unresolved || products.size > 1;
  const identity = !conflict && candidates[0]?.identity;
  const provider = interpretProvider(records, providerBindings, sources, bindingConflict);
  return { sourceEventId: entry?.sourceEventId, productId: identity?.productId || null,
    label: identity ? clientSurfaceLabel({ ...identity, host: dimensions.host.value }) : null,
    categoryId: harnessCategory(identity?.productId, dimensions.host.value, conflict),
    conflict, dimensions, clientEvidence: candidates,
    provider: provider.provider, providerConflict: provider.conflict, providerEvidence: provider.evidence,
    rawEvidence: [...records.values()].flat().map(record => record.fact) };
}

export function sourceClientSummary(entries, { truncated = false } = {}) {
  if (!Array.isArray(entries)) return null;
  const observations = entries.map(interpretSourceObservation);
  const surfaces = [...new Set(observations.map(result => result.label).filter(Boolean))].sort();
  const providers = [...new Set(observations.map(result => result.provider).filter(Boolean))].sort();
  const unavailable = observations.filter(result => !result.label).length;
  const conflicting = observations.filter(result => result.conflict).length;
  const notes = [conflicting ? "conflicting harness evidence" : null,
    unavailable > conflicting || truncated ? "unknown harnesses" : null].filter(Boolean);
  const suffix = notes.length ? `; ${notes.join("; ")}` : "";
  const label = surfaces.length === 1 ? surfaces[0] + suffix
    : surfaces.length > 1 ? `Multiple harnesses${suffix}`
      : conflicting ? HARNESS_CATALOG.statuses.conflicting : HARNESS_CATALOG.statuses.unknown;

  return { label, surfaces, providers, unavailable, conflicting, truncated,
    providerUnavailable: observations.filter(result => !result.provider).length,
    providerConflicting: observations.filter(result => result.providerConflict).length,
    dimensions: summarizeEvidenceDimensions(observations, truncated), observations };
}
